import type { HouseRuleSpec, MatchView, Side } from '../../../engine';
import { BLIND_SWITCH_TURN } from '../../../engine/ledger/horrors';
import { ruleN } from '../../../engine/ledger/state';
import { t } from '../../../i18n';

/**
 * What a House Rule that counts turns has left (issue #10): Short Wick's and the Clock's turns,
 * and when the Blind Eye's dark half moves. Shown on the placard beside the rule's name, so the
 * table's most pressing limit never has to be worked out.
 */
export interface RuleClock {
  /** Short, beside the name: "3/5"; or, after an hourglass, the turns before the Blind Eye moves. */
  readonly text: string;
  readonly hourglass?: boolean;
  /** Read out in full, and shown on hover. */
  readonly label: string;
  /** The last turn it allows has come (or the switch is next). */
  readonly urgent: boolean;
}

export function ruleClock(
  r: HouseRuleSpec,
  v: MatchView,
  names: { side: (s: Side) => string },
): RuleClock | null {
  const l = v.ledger;
  if (!l) return null;
  if (r.id === 'short-wick' || r.id === 'clock') {
    // Short Wick counts the side it is played against; the Clock counts everyone: South's shown.
    const side: Side = r.id === 'clock' ? 'S' : (r.side ?? 'S');
    const n = ruleN(r) ?? 0;
    const used = Math.min(n, l.round.turns[side]);
    return {
      text: `${used}/${n}`,
      label: t('placard.turns', { side: names.side(side), used, n }),
      urgent: used >= n,
    };
  }
  if (r.id === 'blind-eye') {
    // Once the other half has gone dark it stays so to the round's end: nothing to count down.
    const left = BLIND_SWITCH_TURN - v.turn;
    if (left < 0) return null;
    return {
      text: String(left),
      hourglass: true,
      label: left === 0 ? t('placard.darkNextLong') : t('placard.darkInLong', { n: left }),
      urgent: left === 0,
    };
  }
  return null;
}
